import { useEffect, useState } from 'react';
import { postJson } from 'requests/http';
import { trackError } from 'helpers/track_error';
import {
  getGoogleClientId,
  reloadToGetUserGoogleData,
  trackGoogleOneTapClicked,
  trackGoogleOneTapDisplayed,
  trackGoogleOneTapHandshakeSuccess,
  trackGoogleOneTapNotDisplayed,
  sanitizeAndCombineUrl,
} from 'packages/signup/components/signupForm/googleOneTapHelpers';

interface GoogleCredentialEvent {
  credential?: string;
  // eslint-disable-next-line camelcase
  select_by?: string;
}

const useGoogleOneTap = (fromPartner: boolean) => {
  const [googlePromptNotDisplayed, setGooglePromptNotDisplayed] = useState(false);

  function handleGoogleCredentialResponse(e: GoogleCredentialEvent): void {
    if (e.credential) {
      trackGoogleOneTapClicked(e.select_by || '');
      postJson('/send_google_credential', {
        credential: e.credential,
      })
        .then((data: { success: boolean; loginTokenUrl?: string }) => {
          if (data.success && data.loginTokenUrl) {
            // existing user needs to be redirected to ZP and automatically authenticated
            window.location.href = sanitizeAndCombineUrl(
              data.loginTokenUrl,
              window.location.search
            );
          } else if (data.success) {
            // new user
            trackGoogleOneTapHandshakeSuccess();
            reloadToGetUserGoogleData();
          } else {
            setGooglePromptNotDisplayed(true);
            trackError({
              error: new Error('Error in send_google_credential POST request'),
            });
          }
        })
        .catch((error: Error) => {
          setGooglePromptNotDisplayed(true);
          trackError({
            error: new Error('Error in send_google_credential POST request'),
            custom: {
              status: error.name,
              response: error.message,
            },
          });
        });
    }
  }

  useEffect(() => {
    function initializeAndShowGoogleOneTap(): void {
      const clientId = getGoogleClientId();

      if (!clientId || !window.google?.accounts?.id) {
        setGooglePromptNotDisplayed(true);
      } else {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredentialResponse,
          prompt_parent_id: 'google-one-tap-ui',
          cancel_on_tap_outside: false,
          context: 'signup',
          itp_support: true,
        });

        window.google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            setGooglePromptNotDisplayed(true);
            trackGoogleOneTapNotDisplayed(
              notification.getNotDisplayedReason(),
              notification.getDismissedReason()
            );
          } else if (notification.isDisplayed()) {
            trackGoogleOneTapDisplayed();
          }
        });
      }
    }

    if (!fromPartner) {
      initializeAndShowGoogleOneTap();
    }
  }, [fromPartner]);

  const showGoogleOneTap = !googlePromptNotDisplayed && !fromPartner;

  return showGoogleOneTap;
};

export default useGoogleOneTap;
